import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Award,
  TrendingUp,
  Users,
  FileText,
  Filter,
  Plus,
  Eye,
  Download,
  CheckCircle,
  Clock,
  AlertCircle,
  Star
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';

const PerformanceReviewsPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterRating, setFilterRating] = useState('all');

  // Mock Data
  const [stats, setStats] = useState({
    totalReviews: 32,
    averageRating: 4.2,
    onTrial: 3
  });

  const [reviews, setReviews] = useState([]);

  const reviewTypes = [
    { id: 'annual', label: 'تقييم سنوي' },
    { id: 'semi-annual', label: 'تقييم نصف سنوي' },
    { id: 'quarterly', label: 'تقييم ربع سنوي' },
    { id: 'trial', label: 'تقييم تجريبي' }
  ];

  const ratingLevels = [
    { id: 'excellent', label: 'ممتاز', color: 'green', min: 4.5 },
    { id: 'very-good', label: 'جيد جداً', color: 'blue', min: 3.5 },
    { id: 'good', label: 'جيد', color: 'yellow', min: 2.5 },
    { id: 'needs-improvement', label: 'يحتاج تطوير', color: 'orange', min: 1.5 },
    { id: 'poor', label: 'ضعيف', color: 'red', min: 0 }
  ];

  const statuses = [
    { id: 'draft', label: 'مسودة', color: 'gray' },
    { id: 'manager-approved', label: 'معتمد من المدير المباشر', color: 'blue' },
    { id: 'hr-approved', label: 'معتمد من HR', color: 'green' }
  ];

  const departments = ['القسم القانوني', 'القسم الإداري', 'القسم المالي', 'الموارد البشرية'];

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      // Mock reviews data
      setReviews([
        {
          id: 1,
          employee: 'أحمد محمد العتيبي',
          employeeId: 'EMP-001',
          period: '2024 - النصف الأول',
          type: 'semi-annual',
          department: 'القسم القانوني',
          reviewer: 'سعود المطيري',
          reviewDate: '2024-06-30',
          finalScore: 4.7,
          finalRating: 'excellent',
          status: 'hr-approved',
          notes: 'أداء متميز خلال الفترة'
        },
        {
          id: 2,
          employee: 'فاطمة سعيد الغامدي',
          employeeId: 'EMP-002',
          period: '2024 - الربع الثالث',
          type: 'quarterly',
          department: 'القسم الإداري',
          reviewer: 'سعود المطيري',
          reviewDate: '2024-09-30',
          finalScore: 3.8,
          finalRating: 'very-good',
          status: 'manager-approved',
          notes: 'أداء جيد مع ملاحظات بسيطة'
        },
        {
          id: 3,
          employee: 'خالد عبدالله الشمري',
          employeeId: 'EMP-003',
          period: '2024 - السنة الكاملة',
          type: 'annual',
          department: 'القسم المالي',
          reviewer: 'سعود المطيري',
          reviewDate: '2024-12-01',
          finalScore: 4.3,
          finalRating: 'very-good',
          status: 'hr-approved',
          notes: 'أداء ممتاز في معظم المجالات'
        },
        {
          id: 4,
          employee: 'نورة حسن القحطاني',
          employeeId: 'EMP-004',
          period: '2024 - فترة التجربة',
          type: 'trial',
          department: 'القسم القانوني',
          reviewer: 'سعود المطيري',
          reviewDate: '2024-11-30',
          finalScore: 3.2,
          finalRating: 'good',
          status: 'manager-approved',
          notes: 'أداء مقبول - يحتاج متابعة'
        },
        {
          id: 5,
          employee: 'سعود إبراهيم المطيري',
          employeeId: 'EMP-005',
          period: '2024 - النصف الثاني',
          type: 'semi-annual',
          department: 'الموارد البشرية',
          reviewer: 'الإدارة العليا',
          reviewDate: '2024-12-05',
          finalScore: 4.8,
          finalRating: 'excellent',
          status: 'hr-approved',
          notes: 'أداء استثنائي ومتميز'
        },
        {
          id: 6,
          employee: 'منى عبدالرحمن الدوسري',
          employeeId: 'EMP-006',
          period: '2024 - الربع الرابع',
          type: 'quarterly',
          department: 'القسم الإداري',
          reviewer: 'سعود المطيري',
          reviewDate: null,
          finalScore: 0,
          finalRating: null,
          status: 'draft',
          notes: 'قيد الإعداد'
        }
      ]);
      setLoading(false);
    }, 500);
  }, []);

  const getRatingBadge = (ratingId) => {
    const rating = ratingLevels.find(r => r.id === ratingId);
    if (!rating) return null;
    const colors = {
      green: 'bg-green-100 text-green-700 border-green-300',
      blue: 'bg-blue-100 text-blue-700 border-blue-300',
      yellow: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      orange: 'bg-orange-100 text-orange-700 border-orange-300',
      red: 'bg-red-100 text-red-700 border-red-300'
    };
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border-2 ${colors[rating.color]}`}>
        <Star className="w-3 h-3" />
        {rating.label}
      </span>
    );
  };

  const getStatusBadge = (statusId) => {
    const status = statuses.find(s => s.id === statusId);
    const colors = {
      gray: 'bg-gray-100 text-gray-700 border-gray-300',
      blue: 'bg-blue-100 text-blue-700 border-blue-300',
      green: 'bg-green-100 text-green-700 border-green-300'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${colors[status.color]}`}>
        {status.label}
      </span>
    );
  };

  const filteredReviews = reviews.filter(review => {
    const matchesPeriod = filterPeriod === 'all' || review.type === filterPeriod;
    const matchesDepartment = filterDepartment === 'all' || review.department === filterDepartment;
    const matchesRating = filterRating === 'all' || review.finalRating === filterRating;
    return matchesPeriod && matchesDepartment && matchesRating;
  });

  const handleViewDetails = (reviewId) => {
    alert(`عرض تفاصيل التقييم رقم ${reviewId}`);
  };

  const handleExport = () => {
    alert('تصدير تقارير الأداء');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">جاري تحميل بيانات التقييمات...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!hasPermission('hr.performance.view')) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-600">ليس لديك صلاحية الوصول لنظام التقييم والأداء</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-black text-[#1E3A8A] mb-2 flex items-center gap-3">
                <Award className="w-8 h-8" />
                <span>نظام التقييم والأداء الوظيفي</span>
              </h1>
              <p className="text-gray-600">إدارة شاملة لتقييمات الموظفين والأداء</p>
            </div>

            <div className="flex items-center gap-3">
              {hasPermission('hr.performance.create') && (
                <button
                  onClick={() => navigate('/admin/hr/performance/reviews/form')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E3A8A] text-white rounded-lg font-bold hover:bg-[#152d6e] transition-all"
                >
                  <Plus className="w-4 h-4" />
                  <span>تقييم جديد</span>
                </button>
              )}
              {hasPermission('hr.performance.report') && (
                <button
                  onClick={handleExport}
                  className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#C8A870] text-[#C8A870] rounded-lg font-bold hover:bg-[#C8A870] hover:text-white transition-all"
                >
                  <Download className="w-4 h-4" />
                  <span>تصدير</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards - KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Total Reviews */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-[#1E3A8A] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">عدد التقييمات (هذا العام)</p>
                <p className="text-3xl font-black text-[#1E3A8A]">{stats.totalReviews}</p>
              </div>
              <FileText className="w-12 h-12 text-[#1E3A8A] opacity-20" />
            </div>
          </div>

          {/* Average Rating */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-[#C8A870] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">متوسط التقدير العام</p>
                <div className="flex items-center gap-2">
                  <p className="text-3xl font-black text-[#C8A870]">{stats.averageRating}</p>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-5 h-5 ${
                          star <= Math.round(stats.averageRating)
                            ? 'text-[#C8A870] fill-[#C8A870]'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <TrendingUp className="w-12 h-12 text-[#C8A870] opacity-20" />
            </div>
          </div>

          {/* On Trial */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-orange-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">تحت التقييم التجريبي</p>
                <p className="text-3xl font-black text-orange-600">{stats.onTrial}</p>
              </div>
              <AlertCircle className="w-12 h-12 text-orange-600 opacity-20" />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-6">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-600" />
              <select
                value={filterPeriod}
                onChange={(e) => setFilterPeriod(e.target.value)}
                className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none font-semibold"
              >
                <option value="all">جميع الفترات</option>
                {reviewTypes.map((type) => (
                  <option key={type.id} value={type.id}>{type.label}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-600" />
              <select
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
                className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none font-semibold"
              >
                <option value="all">جميع الأقسام</option>
                {departments.map((dept) => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-600" />
              <select
                value={filterRating}
                onChange={(e) => setFilterRating(e.target.value)}
                className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none font-semibold"
              >
                <option value="all">جميع التقديرات</option>
                {ratingLevels.map((rating) => (
                  <option key={rating.id} value={rating.id}>{rating.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Reviews List */}
          {filteredReviews.length > 0 ? (
            <div className="space-y-4">
              {filteredReviews.map((review) => (
                <div
                  key={review.id}
                  className="border-2 border-gray-200 rounded-lg p-4 hover:border-[#1E3A8A] transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-bold text-gray-800 text-lg">{review.employee}</h4>
                        {review.finalRating && getRatingBadge(review.finalRating)}
                        {getStatusBadge(review.status)}
                      </div>
                      <p className="text-sm text-gray-600 mb-1">
                        {review.employeeId} • {review.department}
                      </p>
                      <p className="text-sm text-gray-600">
                        الفترة: {review.period}
                      </p>
                    </div>
                    <button
                      onClick={() => handleViewDetails(review.id)}
                      className="p-2 text-[#1E3A8A] hover:bg-blue-50 rounded-lg transition-colors"
                      title="عرض التفاصيل"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">المسؤول المباشر</p>
                      <p className="text-sm font-bold text-gray-800">{review.reviewer}</p>
                    </div>
                    {review.reviewDate && (
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-600 mb-1">تاريخ التقييم</p>
                        <p className="text-sm font-bold text-gray-800">{review.reviewDate}</p>
                      </div>
                    )}
                    {review.finalScore > 0 && (
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-xs text-gray-600 mb-1">الدرجة النهائية</p>
                        <div className="flex items-center gap-2">
                          <p className="text-2xl font-black text-[#1E3A8A]">{review.finalScore}</p>
                          <span className="text-xs text-gray-600">/ 5</span>
                        </div>
                      </div>
                    )}
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">نوع التقييم</p>
                      <p className="text-sm font-bold text-gray-800">
                        {reviewTypes.find(t => t.id === review.type)?.label}
                      </p>
                    </div>
                  </div>

                  {review.notes && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">ملاحظات</p>
                      <p className="text-sm text-gray-800">{review.notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Award className="w-12 h-12 mx-auto mb-2 text-gray-400" />
              <p>لا توجد تقييمات</p>
            </div>
          )}
        </div>

        {/* Quick Links */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => navigate('/admin/hr/performance/reports')}
            className="p-6 bg-white border-2 border-gray-200 rounded-lg hover:border-[#1E3A8A] transition-all text-right"
          >
            <FileText className="w-8 h-8 text-[#1E3A8A] mb-3" />
            <h4 className="font-bold text-gray-800 mb-1">تقارير الأداء</h4>
            <p className="text-sm text-gray-600">عرض تقارير تفصيلية عن الأداء الوظيفي</p>
          </button>

          <button
            onClick={() => alert('عرض الموظفين تحت خطة التطوير')}
            className="p-6 bg-white border-2 border-gray-200 rounded-lg hover:border-[#1E3A8A] transition-all text-right"
          >
            <Users className="w-8 h-8 text-orange-600 mb-3" />
            <h4 className="font-bold text-gray-800 mb-1">خطط التطوير</h4>
            <p className="text-sm text-gray-600">الموظفون الذين يحتاجون متابعة وتطوير</p>
          </button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default PerformanceReviewsPage;
